import React, { useEffect, useReducer } from "react";
import {
	Row,
	Col,
	Form,
	FormGroup,
	FormControl,
	Button,
	Container,
} from "react-bootstrap";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import { validDocumentUploadTypes } from "../constants/values";
import { useNavigate, useLocation } from "react-router-dom";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import { Link } from 'react-router-dom'

const DocumentUpload = (props) => {
	const initialState = {
		isLoading: true,
		isProfileLoading: true,
		documentUploadTypes: [],
		documentUploadTypeId: "",
		users: [],
		customerCode: "",
		fileUploads: {},
		fileTypeUploadErrors: [],
		overSizeLimit: false,
	};

	const reducer = (state, action) => {
		switch (action.type) {
			case "documentUploadTypes":
				return {
					...state,
					documentUploadTypes: action.payload,
					isLoading: false,
				};
			case "documentUploadTypeId":
				return {
					...state,
					documentUploadTypeId: action.payload,
				};
			case "users":
				return {
					...state,
					users: action.payload,
				};
			case "isProfileLoading":
				return {
					...state,
					isProfileLoading: action.payload,
				};
			case "overSizeLimit":
				return {
					...state,
					overSizeLimit: action.payload,
				};
			case "fileUploads":
				return {
					...state,
					fileUploads: action.payload,
				};
			case "fileTypeUploadErrors":
				return {
					...state,
					fileTypeUploadErrors: action.payload,
				};
			case "documentUploadSubmitted":
				return {
					...state,
					documentUploadSubmitted: action.payload,
					isLoading: false,
				};
			case "resubmitted":
				return {
					...state,
					documentUploadSubmitted: false,
					documentUploadTypeId: "",
					fileUploads: {},
					fileTypeUploadErrors: [],
					overSizeLimit: false,
				};
			case "customerCode":
				return {
					...state,
					customerCode: action.payload,
				};
			case "isLoading":
				return {
					...state,
					isLoading: action.payload,
				};
			case "hideLoader":
				return {
					...state,
					isLoading: false,
				};
			default:
				throw new Error();
		}
	};

	const [uploadState, dispatch] = useReducer(reducer, initialState);
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		async function getDocumentUploadTypes() {
			props.header.setHeader("documentUpload");
			await props.api
				.get(`/api/documents/uploads/types`)
				.then((response) => {
					if (response.status === 200) {
						return response.data;
					}
				})
				.then((data) => {
					dispatch({ type: "documentUploadTypes", payload: data });
				})
				.catch(hideLoader)
				.then(hideLoader);
		}
		getDocumentUploadTypes();
	}, []);

	useEffect(() => {
		async function getCustomerProfile() {
			await props.api
				.get("/api/user")
				.then((response) => {
					if (response.status === 200) {
						return response.data;
					}
				})
				.then((data) => {
					dispatch({ type: "users", payload: data });
					if (data.customers.length === 1) {
						dispatch({ type: "customerCode", payload: data.customers[0].code });
					}
				})
				.then(dispatch({ type: "isProfileLoading", payload: false }))
				.catch(dispatch({ type: "isProfileLoading", payload: false }));
		}
		getCustomerProfile();
	}, []);

	useEffect((prevProps, prevState) => {
		if (
			prevState &&
			prevState.documentUploadSubmitted &&
			!location.pathname.includes("/success")
		) {
			resetSubmitted();
		}
	}, []);

	const handleFiles = (event) => {
		const { files } = event.target;

		dispatch({
			type: "overSizeLimit",
			payload:
				Object.values(files)
					.map((x) => x.size)
					.reduce((a, b) => a + b, 0) /
					1024 /
					1024 >
				50,
		});

		dispatch({
			type: "fileUploads",
			payload: Object.values(files).filter((x) =>
				validDocumentUploadTypes.some((t) => x.name.toLowerCase().endsWith(t))
			),
		});

		dispatch({
			type: "fileTypeUploadErrors",
			payload: Object.values(files).filter(
				(x) =>
					!validDocumentUploadTypes.some((t) =>
						x.name.toLowerCase().endsWith(t)
					)
			),
		});
	};

	const documentUploadRequestIsValid = () => {
		const { fileUploads, documentUploadTypeId, overSizeLimit, customerCode } =
			uploadState;
		const files = Object.entries(fileUploads);
		return (
			files.some((f) => f[1] !== null) &&
			documentUploadTypeId &&
			customerCode &&
			!overSizeLimit
		);
	};

	const handleDocumentUploadSubmitted = async (event) => {
		event.preventDefault();
		const { fileUploads, documentUploadTypeId, customerCode } = uploadState;

		dispatch({ type: "isLoading", payload: true });

		let formData = new FormData();
		Object.keys(fileUploads).forEach((f) => {
			formData.append("file" + f, fileUploads[f], fileUploads[f].name);
		});
		formData.append("documentUploadTypeId", documentUploadTypeId);
		formData.append("customerCode", customerCode);

		await props.api
			.postForm("/api/documents/uploads", formData)
			.then((response) => {
				if (response.status === 200) {
					dispatch({ type: "documentUploadSubmitted", payload: true });
					navigate("/document-upload/success");
				}
			})
			.catch(() => {
				hideLoader();
			})
			.then(() => {
				hideLoader();
			});
	};

	const handleDoctypeChanged = (event) => {
		dispatch({ type: "documentUploadTypeId", payload: event.target.value });
	};

	const getTypeDescription = (description) => {
		if (!description) {
			return "";
		} else {
			return `(${description})`;
		}
	};

	const hideLoader = () => {
		dispatch({ type: "isLoading", payload: false });
	};

	const resetSubmitted = () => {
		dispatch({ type: "resubmitted" });
	};

	const handleCustomerCodeChanged = (event) => {
		dispatch({ type: "customerCode", payload: event.target.value });
	};

	const {
		documentUploadSubmitted,
		fileTypeUploadErrors,
		users,
		customerCode,
		documentUploadTypes,
		documentUploadTypeId,
		overSizeLimit,
	} = uploadState;

	if (uploadState.isLoading || uploadState.isProfileLoading) {
		return <LoadingSpinner />;
	}
	if (documentUploadSubmitted) {
		const documentUploadType = documentUploadTypes.find(
			(d) => d.id.toString() === documentUploadTypeId
		);
		return (
			<div className="section container">
				<Row className="section border-between">
					<Col sm={12} className="contract-details">
						<h2 style={{ marginTop: "30px", marginBottom: "20px" }}>
							We received your {documentUploadType.name} submission.
						</h2>
						<p>
							If we have any questions regarding your submission, we will
							contact you to obtain any additional information needed for
							processing.
						</p>
						<p>
							Need to upload another document?{" "}
							<a className="cursor" onClick={(event) => resetSubmitted()}>
								Click here
							</a>
							.
						</p>
						{users.customers &&
							users.customers.length > 0 &&
							users.acceptCreditCard &&
							users.customers.every((c) =>
								c.providers.every((p) => p.acceptCreditCard)
							) && (
								<div>
									<p>Need to make a credit card payment? Click the button below.</p>
									<div className="panel-body">
										<div className="documents-received mt-3 ms-5">
											<Link to={'/payment'} 
												target='_blank' 
												rel='noopener'
												type="submit"
												formTarget="_blank"
												id="checkout_button"
												className="btn btn-secondary cursor"
												style={{ marginTop: "5px" }}>
												Make a payment
											</Link>
										</div>
									</div>
								</div>
							)}
					</Col>
				</Row>
			</div>
		);
	}

	const startIndex = users.length > 0 && users.customers.length > 1 ? 1 : 0;
	const docTypeIndex = startIndex + 1;
	const fileUploadIndex = startIndex + 2;
	const buttonIndex = startIndex + 3;

	return (
    <>
			<Container>
				<Col>
					<div>
						Only PDF, Word, and Excel documents can be uploaded through this
						secure web page.
					</div>
					<Form
						onSubmit={handleDocumentUploadSubmitted}
						className="processing-form"
					>
						<div className="card processing-doc-type border border-0">
							<div className="card-body">
								{users.customers && users.customers.length > 1 && (
									<FormGroup controlId="customerCode">
										<div className="documents-received">
											1. Select Customer:
										</div>
										<Form.Select
											value={customerCode}
											componentClass="select"
											name="customerCode"
											onChange={handleCustomerCodeChanged}
										>
											<option value=""> </option>
											{users.customers &&
												users.customers.map((c, i) => (
													<option value={c.code} key={i}>
														{c.name}
													</option>
												))}
										</Form.Select>
									</FormGroup>
								)}
								<div className="documents-received mb-3">
									{docTypeIndex}. Select the Document Type:
								</div>
								<FormGroup>
									{documentUploadTypes.map((option, index) => (
										<Form.Check
											key={index}
											value={option.id}
											type="radio"
											name="documentUploadTypeId"
											className="document-type"
											label={`${option.name} ${getTypeDescription(
												option.description
											)}`}
											onChange={handleDoctypeChanged}
											disabled={option.disable === true}
										></Form.Check>
									))}
								</FormGroup>
							</div>
						</div>
						<div className="panel processing-doc-upload">
							<div className="panel-body">
								<div className="documents-received mt-3">
									{fileUploadIndex}. Use the browse button to select the PDF,
									Word, or Excel documents you wish to submit.
								</div>
								<div className="documents-received">
									Upload Documents (50MB size limit):
								</div>
								<FormGroup style={{ display: "inline-flex" }}>
									<FormControl
										type="file"
										multiple
										accept={validDocumentUploadTypes}
										onChange={handleFiles}
									/>
								</FormGroup>
								{overSizeLimit && (
									<div>
										<p>
											The selected files are over the size limit and cannot be
											uploaded
										</p>
									</div>
								)}
								{fileTypeUploadErrors && fileTypeUploadErrors.length > 0 && (
									<div>
										<p>
											The following files have an invalid file type and will not
											be uploaded
										</p>
										<ul>
											{fileTypeUploadErrors.map((error, index) => (
												<li>{error.name}</li>
											))}
										</ul>
									</div>
								)}
								<div className="documents-received mt-3">
									{buttonIndex}. Click the Upload button to submit the selected
									documents.
								</div>
								<div>
                  <button
                    type="submit"
                    className="btn btn-primary cursor"
                    style={{ marginTop: "5px" }}
                    disabled={!documentUploadRequestIsValid()}
                  >
                    Upload
                  </button>
                </div>
							</div>
						</div>
						{users.customers &&
							users.customers.length > 0 &&
							users.acceptCreditCard &&
							users.customers.every((c) =>
								c.providers.every((p) => p.acceptCreditCard)
							) && (
								<div className="panel processing-doc-upload ms-5 border-start border-2">
									<div className="panel-body">
										<div className="documents-received mt-3 ms-5">
											Click the button below to make a credit card
											payment.
										</div>
										<div className="documents-received mt-3 ms-5">
											<Link to={'/payment'} 
												target='_blank' 
												rel='noopener'
												type="submit"
												formTarget="_blank"
												id="checkout_button"
												className="btn btn-secondary cursor"
												style={{ marginTop: "5px" }}>
												Make a payment
											</Link>
										</div>
									</div>
								</div>
							)}
					</Form>
				</Col>
		</Container>
    </>
	);
};

export default withHeaderMessages(WithApiAccess(DocumentUpload));
